import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  CalendarDays,
  Check,
  ChevronsUpDown,
  ClipboardCheck,
  FileText,
  History,
  Plus,
  Star,
  Target,
  User,
  X,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'

import { useLessonSheet } from '../LessonSheetContext'
import { JOURNALS, LESSON_SLOT_LABELS, LESSON_TYPES } from '../../data/subject-teacher-mock'

const RECENT_LESSONS = [
  {
    id: 1,
    title: 'Past continuous — guided practice',
    description: 'Worksheet p. 42 — pair interviews',
    journal: 'English 9B',
    color: '#0274EF',
  },
  {
    id: 2,
    title: 'Vocabulary: Travel & Transport',
    description: 'Matching exercise + listening comprehension',
    journal: 'English 9B',
    color: '#0274EF',
  },
  {
    id: 3,
    title: 'Reading: Short story analysis',
    description: 'Group work on "The Lottery"',
    journal: 'English 9A',
    color: '#27AE60',
  },
]

const DURATION_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
]

export function NewLessonSheet() {
  const { t } = useTranslation()
  const { newLessonOpen, closeNewLesson, initialJournalId } = useLessonSheet()

  const [journalOpen, setJournalOpen] = useState(false)
  const [recentOpen, setRecentOpen] = useState(false)
  const [selectedJournalId, setSelectedJournalId] = useState<number | null>(null)
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [lessonSlot, setLessonSlot] = useState('1')
  const [lessonType, setLessonType] = useState('regular')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [hasPoints, setHasPoints] = useState(false)
  const [hasWorkPlan, setHasWorkPlan] = useState(false)
  const [isIndividual, setIsIndividual] = useState(false)

  const [showHomework, setShowHomework] = useState(false)
  const [hwTitle, setHwTitle] = useState('')
  const [hwDescription, setHwDescription] = useState('')
  const [hwDeadline, setHwDeadline] = useState('')
  const [hwDuration, setHwDuration] = useState('')

  const [showAssessment, setShowAssessment] = useState(false)
  const [assessTitle, setAssessTitle] = useState('')
  const [assessDescription, setAssessDescription] = useState('')
  const [assessDeadline, setAssessDeadline] = useState('')
  const [assessDuration, setAssessDuration] = useState('')
  const [assessAddToTestPlan, setAssessAddToTestPlan] = useState(false)
  const [assessAllowSubmission, setAssessAllowSubmission] = useState(false)

  useEffect(() => {
    if (newLessonOpen) {
      setSelectedJournalId(initialJournalId)
    }
  }, [newLessonOpen, initialJournalId])

  const selectedJournal = JOURNALS.find((j) => j.id === selectedJournalId)

  function resetForm() {
    setSelectedJournalId(null)
    setDate(new Date().toISOString().slice(0, 10))
    setLessonSlot('1')
    setLessonType('regular')
    setTitle('')
    setDescription('')
    setHasPoints(false)
    setHasWorkPlan(false)
    setIsIndividual(false)
    setShowHomework(false)
    setHwTitle('')
    setHwDescription('')
    setHwDeadline('')
    setHwDuration('')
    setShowAssessment(false)
    setAssessTitle('')
    setAssessDescription('')
    setAssessDeadline('')
    setAssessDuration('')
    setAssessAddToTestPlan(false)
    setAssessAllowSubmission(false)
  }

  function handleSave(duplicate = false) {
    toast.success(duplicate ? 'Lesson saved and duplicated' : 'Lesson published')
    resetForm()
    closeNewLesson()
  }

  function handleCancel() {
    resetForm()
    closeNewLesson()
  }

  function applyRecent(lesson: (typeof RECENT_LESSONS)[number]) {
    setTitle(lesson.title)
    setDescription(lesson.description)
    setRecentOpen(false)
  }

  return (
    <Sheet
      open={newLessonOpen}
      onOpenChange={(open) => {
        if (!open) handleCancel()
      }}
    >
      <SheetContent className="flex w-full flex-col sm:max-w-4xl" side="right">
        <SheetHeader>
          <SheetTitle>{t('ekoolTeacher.newLesson', 'New lesson')}</SheetTitle>
          <SheetDescription>
            {t('ekoolTeacher.newLessonDescription', 'Create a new lesson entry for your journal')}
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4 md:flex-row md:gap-0">
          {/* Left Panel — Details */}
          <div className="space-y-4 md:w-72 md:shrink-0 md:pr-6">
            <div className="space-y-1.5">
              <Label>{t('ekoolTeacher.journal', 'Journal')}</Label>
              <Popover open={journalOpen} onOpenChange={setJournalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                  >
                    {selectedJournal ? (
                      <span className="flex items-center gap-2 truncate">
                        <span
                          className="size-3 shrink-0 rounded-full"
                          style={{ backgroundColor: selectedJournal.color }}
                        />
                        {selectedJournal.name}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        {t('ekoolTeacher.selectJournal', 'Select journal…')}
                      </span>
                    )}
                    <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={t('ekoolTeacher.searchJournal', 'Search journals…')}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t('ekoolTeacher.noJournalFound', 'No journal found.')}
                      </CommandEmpty>
                      <CommandGroup>
                        {JOURNALS.map((journal) => (
                          <CommandItem
                            key={journal.id}
                            value={journal.name}
                            onSelect={() => {
                              setSelectedJournalId(journal.id)
                              setJournalOpen(false)
                            }}
                          >
                            <span
                              className="size-3 shrink-0 rounded-full"
                              style={{ backgroundColor: journal.color }}
                            />
                            <span className="flex-1">{journal.name}</span>
                            {selectedJournalId === journal.id && <Check className="size-4" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label>{t('ekoolTeacher.date', 'Date')}</Label>
              <div className="relative">
                <CalendarDays className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input value={date} readOnly className="pl-9" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t('ekoolTeacher.lessonNumber', 'Lesson number')}</Label>
              <Select value={lessonSlot} onValueChange={setLessonSlot}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LESSON_SLOT_LABELS).map(([slot, time]) => (
                    <SelectItem key={slot} value={slot}>
                      {slot}. {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('ekoolTeacher.lessonType', 'Lesson type')}</Label>
              <Select value={lessonType} onValueChange={setLessonType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LESSON_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge
                variant={hasPoints ? 'default' : 'outline'}
                className="cursor-pointer select-none"
                onClick={() => setHasPoints(!hasPoints)}
              >
                <Star className="mr-1 size-3" />
                {t('ekoolTeacher.points', 'Points')}
              </Badge>
              <Badge
                variant={hasWorkPlan ? 'default' : 'outline'}
                className="cursor-pointer select-none"
                onClick={() => setHasWorkPlan(!hasWorkPlan)}
              >
                <Target className="mr-1 size-3" />
                {t('ekoolTeacher.workPlan', 'Work plan')}
              </Badge>
              <Badge
                variant={isIndividual ? 'default' : 'outline'}
                className="cursor-pointer select-none"
                onClick={() => setIsIndividual(!isIndividual)}
              >
                <User className="mr-1 size-3" />
                {t('ekoolTeacher.individual', 'Individual')}
              </Badge>
            </div>
          </div>

          <Separator orientation="vertical" className="hidden md:block" />
          <Separator className="md:hidden" />

          {/* Right Panel — Content */}
          <div className="flex-1 space-y-4 md:pl-6">
            <div className="flex items-start gap-2">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('ekoolTeacher.lessonTitlePlaceholder', 'Lesson title')}
                className="h-auto border-0 px-0 text-lg font-semibold shadow-none focus-visible:ring-0"
              />
              <Popover open={recentOpen} onOpenChange={setRecentOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground shrink-0 gap-1.5"
                  >
                    <History className="size-4" />
                    {t('ekoolTeacher.recentLessons', 'Recent')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <Command>
                    <CommandInput
                      placeholder={t('ekoolTeacher.searchRecent', 'Search recent lessons…')}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t('ekoolTeacher.noRecentFound', 'No recent lessons found.')}
                      </CommandEmpty>
                      <CommandGroup>
                        {RECENT_LESSONS.map((lesson) => (
                          <CommandItem
                            key={lesson.id}
                            value={lesson.title}
                            onSelect={() => applyRecent(lesson)}
                          >
                            <div className="flex-1 space-y-0.5">
                              <p className="text-sm font-medium">{lesson.title}</p>
                              <p className="text-muted-foreground line-clamp-1 text-xs">
                                {lesson.description}
                              </p>
                            </div>
                            <Badge variant="outline" className="ml-2 shrink-0 text-[0.6rem]">
                              <span
                                className="mr-1 size-2 rounded-full"
                                style={{ backgroundColor: lesson.color }}
                              />
                              {lesson.journal}
                            </Badge>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('ekoolTeacher.descriptionPlaceholder', 'Describe the lesson content…')}
              className="min-h-[120px]"
            />

            <div className="flex gap-2">
              {!showHomework && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setShowHomework(true)}
                >
                  <Plus className="size-3.5" />
                  {t('ekoolTeacher.homework', 'Homework')}
                </Button>
              )}
              {!showAssessment && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setShowAssessment(true)}
                >
                  <Plus className="size-3.5" />
                  {t('ekoolTeacher.assessment', 'Assessment')}
                </Button>
              )}
            </div>

            {showHomework && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <FileText className="text-muted-foreground size-4" />
                    {t('ekoolTeacher.homework', 'Homework')}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => {
                      setShowHomework(false)
                      setHwTitle('')
                      setHwDescription('')
                      setHwDeadline('')
                      setHwDuration('')
                    }}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('ekoolTeacher.title', 'Title')}</Label>
                  <Input
                    value={hwTitle}
                    onChange={(e) => setHwTitle(e.target.value)}
                    placeholder={t('ekoolTeacher.hwTitlePlaceholder', 'Homework title')}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('ekoolTeacher.description', 'Description')}</Label>
                  <Textarea
                    value={hwDescription}
                    onChange={(e) => setHwDescription(e.target.value)}
                    placeholder={t(
                      'ekoolTeacher.hwDescriptionPlaceholder',
                      'Describe the homework…'
                    )}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('ekoolTeacher.deadline', 'Deadline')}</Label>
                    <div className="relative">
                      <CalendarDays className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                      <Input
                        type="date"
                        value={hwDeadline}
                        onChange={(e) => setHwDeadline(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('ekoolTeacher.duration', 'Duration')}</Label>
                    <Select value={hwDuration} onValueChange={setHwDuration}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ekoolTeacher.selectDuration', 'Select…')} />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {showAssessment && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <ClipboardCheck className="text-muted-foreground size-4" />
                    {t('ekoolTeacher.assessment', 'Assessment')}
                  </h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => {
                      setShowAssessment(false)
                      setAssessTitle('')
                      setAssessDescription('')
                      setAssessDeadline('')
                      setAssessDuration('')
                      setAssessAddToTestPlan(false)
                      setAssessAllowSubmission(false)
                    }}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('ekoolTeacher.title', 'Title')}</Label>
                  <Input
                    value={assessTitle}
                    onChange={(e) => setAssessTitle(e.target.value)}
                    placeholder={t('ekoolTeacher.assessTitlePlaceholder', 'Assessment title')}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('ekoolTeacher.description', 'Description')}</Label>
                  <Textarea
                    value={assessDescription}
                    onChange={(e) => setAssessDescription(e.target.value)}
                    placeholder={t(
                      'ekoolTeacher.assessDescriptionPlaceholder',
                      'Describe the assessment…'
                    )}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={assessAddToTestPlan}
                      onCheckedChange={(v) => setAssessAddToTestPlan(v === true)}
                    />
                    {t('ekoolTeacher.addToTestPlan', 'Add to test plan')}
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={assessAllowSubmission}
                      onCheckedChange={(v) => setAssessAllowSubmission(v === true)}
                    />
                    {t('ekoolTeacher.allowSubmission', 'Allow submission')}
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('ekoolTeacher.deadline', 'Deadline')}</Label>
                    <div className="relative">
                      <CalendarDays className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                      <Input
                        type="date"
                        value={assessDeadline}
                        onChange={(e) => setAssessDeadline(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('ekoolTeacher.duration', 'Duration')}</Label>
                    <Select value={assessDuration} onValueChange={setAssessDuration}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('ekoolTeacher.selectDuration', 'Select…')} />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <Button variant="ghost" onClick={handleCancel}>
            {t('ekoolTeacher.cancel', 'Cancel')}
          </Button>
          <Button variant="outline" onClick={() => handleSave(true)}>
            {t('ekoolTeacher.saveAndDuplicate', 'Save and duplicate')}
          </Button>
          <Button onClick={() => handleSave(false)}>
            {t('ekoolTeacher.saveAndPublish', 'Save and publish')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
